import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { FileText, Eye, Download, Mail, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ReviewSubmitPage from './ReviewSubmitPage';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';

const AdminSubmissionsTab = ({ submissions }) => {
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null); // PDF URL for modal display
  
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // Search by patient name or doctor name
  const itemsPerPage = 10;

  // Reset to first page when view mode or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm]);
  
  // Filter submissions based on search term
  const filteredSubmissions = useMemo(() => {
    let filtered = [...submissions];
    
    // Filter by search term (patient name or doctor name)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(submission => {
        const patientName = (submission.profiles?.name || '').toLowerCase();
        const doctorName = (submission.doctors?.name || '').toLowerCase();
        return patientName.includes(searchLower) || doctorName.includes(searchLower);
      });
    }
    
    return filtered;
  }, [submissions, searchTerm]);

  const handleView = async (submission) => {
    try {
      // First, check if PDF URL exists in submission data (same as Print button)
      let pdfUrl = submission.pdf_url;
      
      // If not in submission object, fetch full submission data
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }
      
      // If PDF URL exists, show PDF in modal (instead of opening new window)
      if (pdfUrl) {
        // Convert relative URL to absolute if needed
        const absoluteUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
        
        // Store PDF URL and show modal
        setViewingPdfUrl(absoluteUrl);
        setViewingSubmission(submission);
        return; // Exit early - PDF will be shown in modal
      }
      
      // Fallback: If no PDF URL exists, open modal with ReviewSubmitPage (generate/view data)
      setViewingPdfUrl(null); // Clear PDF URL
      setViewingSubmission(submission);
      setLoadingData(true);
      
      try {
        // Fetch full submission data including medical history, dental examination, and procedures
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          // Transform procedures data to match expected format
          // API returns procedures as array, but ReviewSubmitPage expects { rows: [...] } or { procedures: [...] }
          const transformedData = {
            ...data,
            procedures: Array.isArray(data.procedures) 
              ? { rows: data.procedures }
              : (data.procedures || null),
            // Include doctor name for display
            doctor_name: data.doctor_name || submission.doctors?.name || null
          };
          setSubmissionData(transformedData);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to load submission details',
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to load submission details',
          variant: 'destructive'
        });
      } finally {
        setLoadingData(false);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handlePrint = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      if (pdfUrl) {
        // Open PDF in new window for printing
        window.open(pdfUrl, '_blank');
        console.log('Opening PDF URL:', pdfUrl);
      } else {
        toast({
          title: 'Error',
          description: 'PDF not available for this submission',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error printing:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  const handleEmail = async (submission) => {
    try {
      let pdfUrl = submission.pdf_url;
      
      if (!pdfUrl) {
        const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
        if (data.success) {
          pdfUrl = data.pdf_url;
        }
      }

      // Send email - backend will use PDF URL from submission record in database
      const emailData = await apiRequest('email_patient_record.php', {
        method: 'POST',
        body: JSON.stringify({ 
          submission_id: submission.id
          // Note: Backend uses PDF URL from submission record (ensures correct PDF per appointment)
        })
      });
      
      if (emailData.success) {
        toast({
          title: 'Success',
          description: 'Patient record sent via email with PDF download link'
        });
      } else {
        toast({
          title: 'Error',
          description: emailData.error || 'Failed to send email',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send email',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white">Completed Patient's Appointment History</h2>
        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <Label htmlFor="view-mode-submissions" className="text-white text-sm cursor-pointer">
            List View
          </Label>
          <input
            type="checkbox"
            id="view-mode-submissions"
            checked={viewMode === 'list'}
            onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer"
          />
        </div>
      </div>
      
      {/* Search Section */}
      <Card className="glass-effect border-gray-700">
        <CardContent className="p-6">
          <div>
            {/* Search Box */}
            <div>
              <Label htmlFor="search-submissions" className="text-white mb-2 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="search-submissions"
                  type="text"
                  placeholder="Search by patient name or doctor name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="glass-effect border-gray-700">
        <CardContent className="p-6">
          {filteredSubmissions.length > 0 ? (
            viewMode === 'list' ? (
              /* List View with Pagination */
              <>
                <div className="space-y-3">
                  {(() => {
                    const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

                    return paginatedSubmissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                        submission.status === 'completed' ? 'bg-green-600 text-green-100' :
                        submission.status === 'in-progress' ? 'bg-yellow-600 text-yellow-100' :
                        'bg-blue-600 text-blue-100'
                      }`}>
                        {submission.status}
                      </span>
                      <FileText className="h-4 w-4 text-blue-400 flex-shrink-0" />
                      <p className="text-white font-medium truncate text-sm sm:text-base">
                        {submission.profiles?.name || 'Unknown Patient'}
                      </p>
                      {submission.profiles?.patient_unique_id && (
                        <>
                          <span className="text-gray-400 text-sm">({submission.profiles.patient_unique_id})</span>
                        </>
                      )}
                      {(submission.appointment_reference_id || submission.appointment_id) && (
                        <span className="text-blue-400 text-xs font-mono bg-blue-500/20 px-2 py-0.5 rounded flex-shrink-0">
                          {submission.appointment_reference_id || `ID: ${submission.appointment_id}`}
                        </span>
                      )}
                      {submission.doctors?.name && (
                        <>
                          <span className="text-gray-500 hidden sm:inline">•</span>
                          <p className="text-gray-400 text-xs sm:text-sm truncate">
                            Dr. {submission.doctors.name}
                          </p>
                        </>
                      )}
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <p className="text-gray-400 text-xs sm:text-sm truncate">
                        {new Date(submission.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(submission)}
                        className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="View"
                      >
                        <Eye className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrint(submission)}
                        className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Print"
                      >
                        <Download className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Print</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEmail(submission)}
                        className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Email"
                      >
                        <Mail className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Email</span>
                      </Button>
                    </div>
                  </div>
                    ));
                  })()}
                </div>
                
                {/* Pagination Controls for List View */}
                {(() => {
                  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
                  if (filteredSubmissions.length <= itemsPerPage) return null;
                  
                  return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                      <div className="text-sm text-gray-400">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                              return (
                                <Button
                                  key={page}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className={
                                    currentPage === page
                                      ? "bg-blue-600 text-white hover:bg-blue-700"
                                      : "border-gray-600 text-gray-300 hover:bg-gray-700"
                                  }
                                >
                                  {page}
                                </Button>
                              );
                            } else if (
                              page === currentPage - 2 ||
                              page === currentPage + 2
                            ) {
                              return (
                                <span key={page} className="text-gray-500 px-2">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage >= totalPages}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              /* Detailed View with Pagination */
              <>
                <div className="space-y-4">
                  {(() => {
                    const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);
                    
                    return paginatedSubmissions.map((submission) => (
                  <div key={submission.id} className="p-4 bg-gray-800 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-gray-400 text-sm">Patient</p>
                        <div className="flex items-center space-x-2 flex-wrap">
                          <p className="text-white font-medium">{submission.profiles?.name || 'Unknown Patient'}</p>
                          {submission.profiles?.patient_unique_id && (
                            <span className="text-gray-400 text-sm">({submission.profiles.patient_unique_id})</span>
                          )}
                          {(submission.appointment_reference_id || submission.appointment_id) && (
                            <span className="text-blue-400 text-xs font-mono bg-blue-500/20 px-2 py-0.5 rounded">
                              {submission.appointment_reference_id || `ID: ${submission.appointment_id}`}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 text-sm">{submission.profiles?.email || 'No email'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Doctor</p>
                        <p className="text-white">{submission.doctors?.name || 'Not assigned'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Submitted</p>
                        <p className="text-white">{new Date(submission.submitted_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Status</p>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          submission.status === 'completed' ? 'bg-green-600 text-green-100' :
                          submission.status === 'in-progress' ? 'bg-yellow-600 text-yellow-100' :
                          'bg-blue-600 text-blue-100'
                        }`}>
                          {submission.status}
                        </span>
                      </div>
                    </div>
                    
                    {submission.chief_complaint && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-gray-400 text-sm">Chief Complaint</p>
                        <p className="text-white">{submission.chief_complaint}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(submission)}
                        className="flex-1 bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrint(submission)}
                        className="flex-1 bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEmail(submission)}
                        className="flex-1 bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs"
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Email
                      </Button>
                    </div>
                  </div>
                    ));
                  })()}
                </div>
                
                {/* Pagination Controls for Detailed View */}
                {(() => {
                  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
                  if (filteredSubmissions.length <= itemsPerPage) return null;
                  
                  return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                      <div className="text-sm text-gray-400">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                              return (
                                <Button
                                  key={page}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className={
                                    currentPage === page
                                      ? "bg-blue-600 text-white hover:bg-blue-700"
                                      : "border-gray-600 text-gray-300 hover:bg-gray-700"
                                  }
                                >
                                  {page}
                                </Button>
                              );
                            } else if (
                              page === currentPage - 2 ||
                              page === currentPage + 2
                            ) {
                              return (
                                <span key={page} className="text-gray-500 px-2">
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage >= totalPages}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )
          ) : (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {searchTerm 
                  ? 'No submissions found matching your search' 
                  : 'No submissions yet'}
              </p>
              <p className="text-gray-500">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Submissions will appear here once patients start using the system'}
              </p>
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={viewingSubmission !== null} onOpenChange={(open) => {
        if (!open) {
          setViewingSubmission(null);
          setViewingPdfUrl(null); // Clear PDF URL when modal closes
        }
      }}>
        <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-7xl w-[98vw] max-h-[95vh] p-0 flex flex-col [&>button]:hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white text-xl">
                Patient Record and Invoice
                {viewingSubmission?.appointment_reference_id && (
                  <span className="text-gray-400 text-sm ml-2">
                    - {viewingSubmission.appointment_reference_id}
                  </span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {viewingPdfUrl && !isMobile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (viewingPdfUrl) {
                        // Extract filename from URL to maintain consistency with uploaded filename
                        let filename = 'Patient-Record.pdf'; // Default fallback
                        
                        try {
                          // Extract filename from URL path
                          const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                          const pathParts = urlPath.split('/');
                          const urlFilename = pathParts[pathParts.length - 1];
                          
                          // If filename exists in URL, use it; otherwise use default
                          if (urlFilename && urlFilename.endsWith('.pdf')) {
                            filename = urlFilename;
                          } else {
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                        } catch (error) {
                          console.error('Error extracting filename from URL:', error);
                          // Fallback: construct from submission data
                          filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                        }
                        
                        const link = document.createElement('a');
                        link.href = viewingPdfUrl;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        toast({
                          title: 'Download started',
                          description: 'Your PDF is being downloaded',
                        });
                      }
                    }}
                    className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setViewingSubmission(null);
                    setViewingPdfUrl(null);
                  }}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* PDF Viewer - If PDF URL exists, show PDF */}
          {viewingPdfUrl ? (
            isMobile ? (
              // Mobile: Open PDF in new window (better compatibility with Safari/Chrome mobile)
              <div 
                className="flex-1 flex flex-col items-center justify-center p-6"
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)'
                }}
              >
                <div className="text-center space-y-4">
                  <FileText className="h-16 w-16 text-blue-400 mx-auto" />
                  <h3 className="text-white text-lg font-semibold">View PDF on Mobile</h3>
                  <p className="text-gray-300 text-sm max-w-md">
                    For the best viewing experience on mobile devices, the PDF will open in a new window where you can view all pages and use native PDF controls.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <Button
                      onClick={() => {
                        window.open(viewingPdfUrl, '_blank');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
                      size="lg"
                    >
                      <Eye className="h-5 w-5 mr-2" />
                      Open PDF in New Window
                    </Button>
                    <Button
                      onClick={() => {
                        if (viewingPdfUrl) {
                          // Extract filename from URL to maintain consistency with uploaded filename
                          let filename = 'Patient-Record.pdf'; // Default fallback
                          
                          try {
                            // Extract filename from URL path
                            const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
                            const pathParts = urlPath.split('/');
                            const urlFilename = pathParts[pathParts.length - 1];
                            
                            // If filename exists in URL, use it; otherwise use default
                            if (urlFilename && urlFilename.endsWith('.pdf')) {
                              filename = urlFilename;
                            } else {
                              // Fallback: construct from submission data
                              filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                            }
                          } catch (error) {
                            console.error('Error extracting filename from URL:', error);
                            // Fallback: construct from submission data
                            filename = `Patient-Record-${viewingSubmission?.profiles?.name || 'Record'}-${viewingSubmission?.appointment_reference_id || 'N/A'}.pdf`;
                          }
                          
                          const link = document.createElement('a');
                          link.href = viewingPdfUrl;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          toast({
                            title: 'Download started',
                            description: 'Your PDF is being downloaded',
                          });
                        }
                      }}
                      variant="outline"
                      className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white px-6 py-3"
                      size="lg"
                    >
                      <Download className="h-5 w-5 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Desktop: Use embed tag for better performance and native PDF rendering
              <div 
                className="flex-1 overflow-y-auto overflow-x-hidden" 
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  maxHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)',
                  WebkitOverflowScrolling: 'touch',
                  position: 'relative'
                }}
              >
                <embed
                  src={`${viewingPdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-width`}
                  type="application/pdf"
                  className="w-full"
                  style={{ 
                    minHeight: 'calc(95vh - 120px)',
                    width: '100%',
                    display: 'block',
                    border: 'none'
                  }}
                />
              </div>
            )
          ) : (
            <div className="flex-1 overflow-y-auto px-6 py-4" id="pdf-content-wrapper">
              {loadingData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              ) : submissionData ? (
                <ReviewSubmitPage
                  patientId={submissionData.patient_id}
                  patientName={submissionData.patient_name}
                  appointmentId={submissionData.appointment_id || viewingSubmission?.appointment_id || null}
                  medicalHistory={submissionData.medical_history}
                  dentalExamination={submissionData.dental_examination}
                  procedures={
                    Array.isArray(submissionData.procedures) 
                      ? { rows: submissionData.procedures }
                      : submissionData.procedures || null
                  }
                  hideDialog={true}
                  onBack={null}
                  onSubmit={null}
                  doctorName={submissionData.doctor_name || viewingSubmission?.doctors?.name || null}
                />
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubmissionsTab;